import { useRef, useState } from "react";
import { Upload, X, Link as LinkIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ImageUpload({ value, onChange, label, testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <div className="tech-label">{label}</div>}
      <div className="flex gap-3 flex-col sm:flex-row">
        <div className="flex-shrink-0">
          <div className="h-24 w-24 sm:h-28 sm:w-28 border border-zinc-300 bg-zinc-100 flex items-center justify-center overflow-hidden">
            {value ? (
              <img src={value} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-zinc-400">No image</span>
            )}
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 text-sm font-bold disabled:opacity-60"
              data-testid={`${testId}-upload-btn`}
            >
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="inline-flex items-center gap-1 border border-zinc-300 hover:border-brand-red hover:text-brand-red text-zinc-700 px-3 py-2 text-sm"
              >
                <X size={14} /> Remove
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
          />
          <div className="relative">
            <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid={`${testId}-url-input`}
              placeholder="or paste image URL"
              value={value && value.startsWith("data:") ? "" : value || ""}
              onChange={(e) => onChange(e.target.value)}
              className="pl-9 h-10 rounded-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
